import React, { useMemo } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import download from 'downloadjs'
import clsx from 'clsx'
import { isUndefined } from 'lodash'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { receiptStates } from '@/modules/base/util/constants'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import SuccessIcon from '@assets/images/icons/check_circle.svg'
import ErrorIcon from '@assets/images/icons/v2/error-full.svg'
import ReceiptIcon from '@assets/images/icons/document_blank_fill.svg'
import ReceiptExistsIcon from '@assets/images/icons/gini_document.svg'
import DownloadIcon from '@assets/images/icons/download.svg'
import InfoIcon from '@assets/images/icons/info-icon.svg'
import customDecodeUri from '@/utils/customDecodeUri'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import ReceiptAnalyseResultTooltip from './receipt-analyse-result-tooltip'
// FLAG:useDocuAi — names the e-invoice format behind the validity state.
import { getEInvoiceFormatLabel } from '@/modules/document/utils/e-invoice-format'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  card: {
    position: 'relative',
    background: theme.white,
    padding: '15px 30px',
    marginBottom: 30,
    borderRadius: 4,
    border: theme.border,
  },
  label: {
    fontWeight: theme.fontWeights.medium,
    marginLeft: 10,
  },
  infoIcon: {
    marginLeft: 5,
    cursor: 'help',
  },
  tooltip: {
    maxWidth: 500,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  empty: {
    justifyContent: 'center',
  },
  state: {
    display: 'flex',
    alignItems: 'center',
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  actionLabel: {
    marginLeft: 5,
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smaller,
  },
  foundValues: {
    fontSize: theme.fontSizes.smaller,
    fontWeight: theme.fontWeights.medium,
  },
}))

const priceValueToCount = [
  'priceTotal',
  'priceNet',
  'priceGross',
]
const vatValuesToCount = [
  'vat',
  'vatAmount',
]
const valuesToCount = [
  'supplierId',
  'customerId',
  'receiptNumber',
  'date',
  'deliveryDate',
  'description',
  'dueDate',
  'payKindId',
]

const payeeValuesToCount = [
  'payee',
  'supplierId',
  'sender',
  'supplier',
  'customerId',
  'customer',
]

const ReceiptAnalyseResult = (props) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const { validationResult: validationResultFromAnalysis, eInvoice } = useSelector(state => state.receipt.edit.data)
  // FLAG:useDocuAi — the format behind the verdict ('ZUGFeRD', 'XRechnung', …). Only ever present
  // for useDocuAi tenants (serialize.js attaches it), so every other render is unchanged.
  const eInvoiceFormatLabel = getEInvoiceFormatLabel(eInvoice)
  const withFormat = (label: string) => (eInvoiceFormatLabel ? `${label} (${eInvoiceFormatLabel})` : label)
  const { id, state, receipts = [], identifiedValues = [], filledData = [], ocrDataChanged } = props

  const receiptWithResult = useMemo(() => {
    return receipts.find((receipt) => receipt.validationResult)
  }, [receipts])

  const stateOptions = useMemo(() => {
    if (!validationResultFromAnalysis && !receiptWithResult && !identifiedValues.length && !filledData.length) {
      // FLAG:useDocuAi — a recognised e-invoice says so even when KoSIT produced no verdict
      // (validator not configured, or an analysis from before the summary was emitted): the
      // reviewer must be able to tell XML-sourced data from an OCR reading at a glance.
      if (eInvoiceFormatLabel) {
        return {
          fill: theme.primarySuperLight,
          icon: ReceiptExistsIcon,
          label: `${lang.receipt.analyseResult.eInvoiceDetected} (${eInvoiceFormatLabel})`,
        }
      }
      return {
        fill: theme.primarySuperLight,
        icon: receipts.length ? ReceiptExistsIcon : ReceiptIcon,
        label: receipts.length ? lang.receipt.analyseResult.exists : lang.receipt.analyseResult.empty,
      }
    }
    if (receiptWithResult || validationResultFromAnalysis) {
      const { validationResult } = receiptWithResult || { validationResult: validationResultFromAnalysis }
      return {
        fill: validationResult.valid ? theme.green : theme.error,
        icon: validationResult.valid ? SuccessIcon : ErrorIcon,
        // FLAG:useDocuAi — withFormat is a no-op without an eInvoice block (legacy renders).
        label: withFormat(validationResult.valid ? lang.receipt.analyseResult.valid : lang.receipt.analyseResult.invalid),
      }
    }

    return {
      fill: theme.green,
      icon: SuccessIcon,
      label: withFormat(lang.receipt.analyseResult.success),
    }
  }, [receipts, receiptWithResult, identifiedValues, filledData, validationResultFromAnalysis, eInvoiceFormatLabel])

  const autoFilledValuesCount = useMemo(() => {
    if (state !== receiptStates.DRAFT && id && !ocrDataChanged) { return }
    const values = identifiedValues.length ? identifiedValues : filledData
    if (!values.length) { return 0 }
    const countedValues = values.filter((value) => valuesToCount.includes(value)).length
    const priceValueFound = values.some((value) => priceValueToCount.includes(value))
    const vatValueFound = values.some((value) => vatValuesToCount.includes(value))
    const payeeValueFound = values.some((value) => payeeValuesToCount.includes(value))

    return countedValues + (priceValueFound ? 1 : 0) + (vatValueFound ? 1 : 0) + (payeeValueFound ? 1 : 0)
  }, [identifiedValues, filledData, state, id])

  const onClickDownload = async() => {
    try {
      const response = await FileService.getXmlValidationReport(receiptWithResult?.fileId ?? receiptWithResult?.id)
      const blob = new Blob([response.body], { type: 'text/html;charset=utf-8' })
      const fileName = customDecodeUri(receiptWithResult?.name.split('.')[0])
      download(blob, `${fileName}-report`, response.response.headers['content-type'])
    } catch (error) {
      toast.showToast({ type: 'error', message: lang.receipt.analyseResult.errors.download })
    }
  }

  const action = useMemo(() => {
    if (!receiptWithResult && !identifiedValues.length && !filledData.length) { return null }
    if (!receiptWithResult && isUndefined(autoFilledValuesCount)) { return null }

    if (!receiptWithResult) {
      return (
        <div className={classes.foundValues}>
          {lang.receipt.analyseResult.foundValues(autoFilledValuesCount)}
        </div>
      )
    }

    return (
      <div className={classes.action} onClick={onClickDownload}>
        <SVGIcon svg={DownloadIcon} height='10px' width='10px' />
        <div className={classes.actionLabel}>{lang.receipt.analyseResult.download}</div>
      </div>
    )
  }, [receiptWithResult, identifiedValues, filledData, autoFilledValuesCount])

  return (
    <div className={classes.card}>
      <div className={clsx(classes.content, (!receiptWithResult && !identifiedValues.length && !filledData.length) && classes.empty)}>
        <div className={classes.state}>
          <SVGIcon svg={stateOptions.icon} height='20px' width='20px' fill={stateOptions.fill} />
          <div className={classes.label}>{stateOptions.label}</div>
          {receiptWithResult && (
            <RichTooltip
              content={<ReceiptAnalyseResultTooltip />}
              styles={{ tooltip: classes.tooltip }}
              width='auto'
              placement='bottom'>
              <SVGIcon className={classes.infoIcon} svg={InfoIcon} height='13px' width='13px' />
            </RichTooltip>
          )}
        </div>
        {action}
      </div>
    </div>
  )
}

export default ReceiptAnalyseResult
